<template>
    <vue-scroll class="pb-20" v-if="newsletter">
        <template v-if="newsletter.has_pending_order">
            <el-alert
                :closable="false"
                :title="
                    'Editing for newsletter is disabled. Order for ' +
                    newsletter.formatted_month +
                    ' is already submitted.'
                "
                center
                class="text-center"
                show-icon
                style="max-width: 800px; margin: 50px auto"
                type="warning"
            >
            </el-alert>
        </template>

        <div v-show="!newsletter.has_pending_order">
            <div class="page-header">
                <h1>Newsletter: {{ newsletter.formatted_month }}</h1>
            </div>

            <div style="position: absolute; width: calc(100% - 40px)">
                <el-steps
                    class="creation-steps"
                    :active="currentStep - 1"
                    align-center
                >
                    <el-step
                        @click.self="clickStep(index)"
                        :description="step"
                        v-for="(step, index) in steps"
                        :key="step"
                        :status="getStepStatus(index)"
                        :class="{ done: currentStep > index + 1 }"
                    >
                        <a
                            href="#"
                            @click.prevent="clickStep(index)"
                            slot="icon"
                        >
                            <i
                                class="el-step__icon-inner is-status el-icon-check"
                                v-if="currentStep > index + 1"
                            />
                            <span v-else>{{ index + 1 }}</span>
                        </a>
                        <template v-slot:description>
                            <a
                                href="#"
                                title="Click to go to this step"
                                @click.prevent="clickStep(index)"
                                >{{ step }}</a
                            >
                        </template>
                    </el-step>
                </el-steps>
            </div>

            <div class="action-buttons">
                <el-button
                    class="float-left"
                    type="default"
                    @click="prev"
                    icon="el-icon-arrow-left"
                    :disabled="!canPrev"
                >
                    Previous
                </el-button>
                <div class="float-left ml-10 mt-15">
                    <span
                        :class="{ 'text-error': exceedsMaxCharCount }"
                        v-if="maxCharCount"
                    >
                        Master Character Count: <b>{{ masterCharCount }}</b
                        >/{{ maxCharCount }}
                    </span>
                    <span class="ml-16" v-if="maxUploads">
                        Photo Count: <b>{{ masterUploadCount }}</b
                        >/{{ maxUploads }}
                    </span>
                </div>
                <el-button
                    class="float-right el-button--primary"
                    type="primary"
                    @click="next"
                    v-if="canNext"
                    :loading="form.busy"
                    icon="mdi mdi-content-save"
                    >Save and Proceed
                </el-button>
                <transition name="el-fade-in">
                    <div
                        class="float-right mr-10 mt-8 success-text"
                        v-show="show_auto_save_message"
                    >
                        <i class="mdi mdi-check-circle" /> Auto-saved
                    </div>
                </transition>
                <transition name="el-fade-in">
                    <div
                        class="float-right mr-10 mt-8 text-error"
                        v-show="show_auto_save_error"
                    >
                        <i class="mdi mdi-alert-circle" /> Can't auto-save
                    </div>
                </transition>
                <transition name="el-fade-in">
                    <span
                        class="ml-10 mt-8 warning-text"
                        v-show="show_auto_save_validation_message"
                    >
                        <i class="mdi mdi-alert-circle" /> Can't auto-save,
                        please check validation errors.
                    </span>
                </transition>
                <transition name="el-fade-in">
                    <div
                        class="float-right mr-10 mt-8 warning-text"
                        v-show="typing_active && hasChanges"
                    >
                        <i class="mdi mdi-keyboard"></i> Typing... auto-save
                        paused
                    </div>
                </transition>
                <div
                    class="float-right mr-10 mt-8 warning-text"
                    v-show="
                        !show_auto_save_error &&
                        !show_auto_save_message &&
                        hasChanges
                    "
                >
                    You have unsaved changes.
                </div>
                <div class="clearfix"></div>
            </div>

            <div
                class="text-error exceeds-max-char-message"
                v-if="exceedsMaxCharCount"
            >
                <i class="mdi mdi-alert-circle" />
                Your newsletter's total character count may not be greater than
                {{ maxCharCount }} characters.
            </div>
            <div
                class="text-error exceeds-max-char-message"
                v-else-if="form.errors.has('_others')"
            >
                <i class="mdi mdi-alert-circle" />
                {{ form.errors.get("_others") }}
            </div>
            <div
                class="warning-text exceeds-max-char-message"
                v-else-if="additionalCharCost > 0"
            >
                <i class="mdi mdi-alert-circle" />
                You have reached a character count limit. Additional characters
                will result in a price increase of
                {{ additionalCharCost | currency }}.
            </div>

            <p class="mt-5 mb-5" v-if="currentStep === 1">
                If you have articles and/or photos for your newsletter, please
                enter them below.
                <br />If you do not have any articles or photos this month,
                Corwin will supply them for you.
            </p>

            <span
                :class="{ 'text-error': checkCorrespondingInfo }"
                v-if="checkCorrespondingInfo"
                ><i class="mdi mdi-alert-circle" /> Corresponding information
                text box cannot be left blank in {{ CorrespondingInfoError }}
            </span>

            <div class="mt-10">
                <articles
                    :form="form"
                    :newsletter="newsletter"
                    @save="next()"
                    @typing-activity="trackTypingActivity"
                    ref="articles"
                    v-if="currentStep === 1"
                />
                <lists
                    :form="form"
                    :newsletter="newsletter"
                    v-if="currentStep === 2"
                    @typing-activity="trackTypingActivity"
                />
                <dept-list
                    :form="form"
                    v-if="currentStep === 3"
                    :newsletter="newsletter"
                />
                <filler-articles
                    :form="form"
                    v-if="currentStep === 4"
                    :newsletter="newsletter"
                />
                <proof
                    :form="form"
                    v-if="currentStep === 5"
                    :newsletter="newsletter"
                />
                <place-order
                    v-if="currentStep === 6"
                    :newsletter="newsletter"
                />
            </div>
        </div>
    </vue-scroll>
</template>
<script>
import unsaved_changes_mixin from "@/mixins/unsaved-changes";
import AdditionalList from "./single/AdditionalList";
import Articles from "./single/Articles";
import DeptList from "./single/DeptList";
import FillerArticles from "./single/FillerArticles";
import Lists from "./single/Lists";
import PlaceOrder from "./single/PlaceOrder";
import Proof from "./single/Proof";
import SpecialEvents from "./single/SpecialEvents";

export default {
    mixins: [unsaved_changes_mixin],
    components: {
        Articles,
        SpecialEvents,
        AdditionalList,
        DeptList,
        Lists,
        Proof,
        PlaceOrder,
        FillerArticles
    },
    beforeRouteEnter(to, from, next) {
        Promise.all([
            axios.get(`/web-api/newsletter/${to.params.id}`, {
                params: {
                    include:
                        "articles.uploads,special_events,list_two_items,dept_list_items,filler_articles,photo_pages,community"
                }
            }),
            axios.get(`/web-api/filler-article`)
        ]).then((responses) => {
            next((vm) => vm.setData(responses));
        });
    },
    data() {
        return {
            newsletter: null,
            steps: [
                "Enter Your Articles",
                "Lists",
                "Dept. List",
                "Select Filler Articles",
                "Proof My Order",
                "Place My Order"
            ],
            currentStep: 1,
            form: new Form({
                proofed: false,
                upload_photo_pages: [],
                remove_photo_page: false,
                list_1_headline: null,
                list_2_headline: null,
                dept_list_headline: null,
                articles: [],
                special_events: [],
                list_two_items: [],
                dept_list_items: [],
                filler_articles: [],
                filler_as_cover: false
            }),
            filler_articles: [],
            auto_save_interval: null,
            auto_saving: false,
            show_auto_save_message: false,
            show_auto_save_error: false,
            show_auto_save_validation_message: false,
            CorrespondingInfoError: null,
            lastTypingTimestamp: null,
            typing_active: false
        };
    },
    created() {
        window.Bus.$once("error:order_pending_for_current_month", () => {
            this.stopAutoSave();
        });
    },
    mounted() {
        this.initAutoSave();
        //if step parameter is set, then to that step
        if (this.$route.query.step) {
            this.currentStep = parseInt(this.$route.query.step);
        }
    },
    beforeDestroy() {
        this.stopAutoSave();
    },
    beforeRouteLeave(to, from, next) {
        this.stopAutoSave();
        next();
    },
    computed: {
        canNext() {
            return this.currentStep < this.steps.length;
        },
        canPrev() {
            return this.currentStep > 1;
        },
        maxCharCount() {
            if (!this.newsletter) {
                return null;
            }
            if (!this.newsletter.community.newsletter_type_id) {
                return null;
            }
            return this.$store.state.config.settings.newsletter.max_char[
                this.newsletter.community.newsletter_type_id
            ];
        },
        masterCharCount() {
            let count =
                Utils.length(this.form.list_1_headline) +
                Utils.length(this.form.list_2_headline) +
                Utils.length(this.form.dept_list_headline);
            count += _.sumBy(this.form.articles, (article) => {
                return (
                    Utils.length($(article.body).text()) +
                    Utils.length(article.headline)
                );
            });
            count += _.sumBy(this.form.special_events, (event) => {
                return (
                    Utils.length(event.value_1) + Utils.length(event.value_2)
                );
            });
            count += _.sumBy(this.form.list_two_items, (item) => {
                return Utils.length(item.value);
            });
            count += _.sumBy(this.form.dept_list_items, (item) => {
                return Utils.length(item.value_1) + Utils.length(item.value_2);
            });
            return count;
        },
        exceedsMaxCharCount() {
            if (this.maxCharCount === 11000) {
                let count = this.maxCharCount + 100;
                return this.masterCharCount > count;
            }
            return this.masterCharCount > this.maxCharCount;
        },
        checkCorrespondingInfo() {
            // Check Special Events
            for (var i = 0; i < this.form.special_events.length; i++) {
                if (
                    this.form.special_events[i].value_1 &&
                    !this.form.special_events[i].value_2
                ) {
                    this.CorrespondingInfoError = "List 1";
                    return true;
                }
                if (
                    this.form.special_events[i].value_2 &&
                    !this.form.special_events[i].value_1
                ) {
                    this.CorrespondingInfoError = "List 1";
                    return true;
                }
            }
            // Check Department List Items
            for (var i = 0; i < this.form.dept_list_items.length; i++) {
                if (
                    this.form.dept_list_items[i].value_1 &&
                    !this.form.dept_list_items[i].value_2
                ) {
                    this.CorrespondingInfoError = "Department List";
                    return true;
                }
                if (
                    this.form.dept_list_items[i].value_2 &&
                    !this.form.dept_list_items[i].value_1
                ) {
                    this.CorrespondingInfoError = "Department List";
                    return true;
                }
            }
            this.CorrespondingInfoError = null;
        },
        hasValidationErrors() {
            return this.exceedsMaxCharCount || this.checkCorrespondingInfo;
        },
        breakpoints() {
            return this.$store.state.config.settings.newsletter
                .additional_cost_breakpoints[
                this.newsletter.community.newsletter_type_id
            ];
        },
        additionalCharCost() {
            if (!this.breakpoints) {
                return 0;
            }
            let cost = 0;
            this.breakpoints.forEach((breakpoint) => {
                if (this.masterCharCount > breakpoint) {
                    cost +=
                        this.$store.state.config.settings.newsletter
                            .additional_cost;
                }
            });
            return cost;
        },
        masterUploadCount() {
            return _.sumBy(
                this.form.articles,
                (article) => article.uploads.length
            );
        },
        maxUploads() {
            if (!this.newsletter) {
                return null;
            }
            if (!this.newsletter.community.newsletter_type_id) {
                return null;
            }
            return this.$store.state.config.settings.newsletter.max_photos;
        }
    },
    methods: {
        getCleanFormData() {
            return JSON.stringify(
                _.pick(this.form, [
                    "list_1_headline",
                    "list_2_headline",
                    "dept_list_headline",
                    "proofed",
                    "articles",
                    "special_events",
                    "list_two_items",
                    "dept_list_items",
                    "filler_articles",
                    "filler_as_cover"
                ])
            );
        },
        hasAttributeChanges(attributes) {
            let old_data = JSON.stringify(
                _.pick(JSON.parse(this.oldData), attributes)
            );
            return old_data !== JSON.stringify(_.pick(this.form, attributes));
        },
        getChangedFormAttributes() {
            let attrs = [
                "list_1_headline",
                "list_2_headline",
                "dept_list_headline",
                "proofed",
                "articles",
                "special_events",
                "list_two_items",
                "dept_list_items",
                "filler_articles",
                "filler_as_cover"
            ];
            let changed = [];
            attrs.forEach((attr) => {
                if (this.hasAttributeChanges([attr])) {
                    changed.push(attr);
                }
            });
            return changed;
        },
        setData(responses) {
            this.newsletter = responses[0].data;
            _.extend(
                this.form,
                _.pick(this.newsletter, [
                    "list_1_headline",
                    "list_2_headline",
                    "dept_list_headline",
                    "proofed",
                    "filler_as_cover"
                ])
            );
            // articles
            this.newsletter.articles.data.forEach((item) => {
                let body = $(`<div>${item.body}</div>`).html();
                this.form.articles.push({
                    ..._.pick(item, [
                        "id",
                        "newsletter_id",
                        "headline",
                        "is_recurring"
                    ]),
                    body,
                    uploads: _.map(item.uploads.data, (upload) => {
                        return _.pick(upload, ["id", "url"]);
                    })
                });
            });
            let length;
            if (!this.newsletter.articles.data.length) {
                length = this.form.articles.length;
                for (let i = 0; i < 4 - length; i++) {
                    this.form.articles.push({
                        id: null,
                        newsletter_id: this.newsletter.id,
                        headline: null,
                        body: "",
                        is_recurring: false,
                        uploads: []
                    });
                }
            }

            // special events
            this.newsletter.special_events.data.forEach((item) => {
                this.form.special_events.push(
                    _.pick(item, ["id", "newsletter_id", "value_1", "value_2"])
                );
            });
            length = this.form.special_events.length;
            for (let i = 0; i < 7 - length; i++) {
                this.form.special_events.push({
                    newsletter_id: this.newsletter.id,
                    id: null,
                    value_1: null,
                    value_2: null
                });
            }

            // additional list
            this.newsletter.list_two_items.data.forEach((item) => {
                this.form.list_two_items.push(
                    _.pick(item, ["id", "newsletter_id", "value"])
                );
            });
            length = this.form.list_two_items.length;
            for (let i = 0; i < 7 - length; i++) {
                this.form.list_two_items.push({
                    newsletter_id: this.newsletter.id,
                    id: null,
                    value: null
                });
            }

            // dept list
            this.newsletter.dept_list_items.data.forEach((item) => {
                this.form.dept_list_items.push(
                    _.pick(item, ["id", "newsletter_id", "value_1", "value_2"])
                );
            });
            length = this.form.dept_list_items.length;
            for (let i = 0; i < 7 - length; i++) {
                this.form.dept_list_items.push({
                    newsletter_id: this.newsletter.id,
                    id: null,
                    value_1: null,
                    value_2: null
                });
            }

            // filler articles
            this.form.filler_articles =
                this.newsletter.filler_articles.data.map((item) =>
                    _.pick(item, ["filler_article_id", "priority"])
                );
            this.filler_articles = responses[1].data.data;
            this.form.filler_articles = this.filler_articles.map(
                (filler_article) => {
                    return {
                        filler_article,
                        filler_article_id: filler_article.id,
                        priority: _.get(
                            this.form.filler_articles.find(
                                (item) =>
                                    item.filler_article_id === filler_article.id
                            ),
                            "priority",
                            null
                        )
                    };
                }
            );

            this.initOldData();
        },
        prev() {
            this.currentStep--;
        },
        /**
         * Moves to the next step of the newsletter editing process.
         * If there are validation errors, it logs a message and returns.
         * Otherwise, it saves the articles, lists, department list items, and filler articles,
         * and updates the current step accordingly.
         * If the current step is 5 and the "I have proofed my newsletter" checkbox is not checked,
         * it displays an alert and returns.
         * If the current step is 5 and the checkbox is checked, it forgets any form errors,
         * proofs the newsletter, and updates the current step.
         * @returns {void}
         */
        next() {
            if (this.hasValidationErrors) {
                console.log("has validation errors");
                return;
            }
            let updateCurrentStep = (response = null) => {
                this.$notify.success({
                    message: `Changes have been saved.`
                });
                this.currentStep++;
                if (response) {
                    this.form.proofed = response.data.proofed;
                }
                this.initOldData();
            };
            switch (this.currentStep) {
                case 1:
                    this.closePopup();
                    this.saveArticles().then(updateCurrentStep);
                    break;
                case 2:
                    this.saveLists().then(updateCurrentStep);
                    break;
                case 3:
                    this.saveDeptListItems().then(updateCurrentStep);
                    break;
                case 4:
                    this.saveFillerArticles().then(updateCurrentStep);
                    break;
                case 5:
                    if (!this.form.proofed) {
                        this.$alert(
                            `You must check "I have proofed my newsletter" checkbox to proceed.`,
                            "Oops!",
                            {
                                confirmButtonText: "OK"
                            }
                        );
                        return false;
                    }
                    this.form.errors.forget();
                    this.proofNewsletter().then(updateCurrentStep);
                    break;
                default:
                    this.currentStep++;
                    break;
            }
        },
        proofNewsletter() {
            return window.App.putData(
                `/web-api/newsletter/${this.newsletter.id}?include=photo_pages`,
                this.form,
                null,
                ["proofed", "remove_photo_page", "upload_photo_pages"]
            ).then((response) => {
                this.form.upload_photo_pages = [];
                this.form.remove_photo_page = false;
                _.extend(
                    this.newsletter,
                    _.pick(response.data, ["has_photo_page", "photo_pages"])
                );
                return response;
            });
        },
        saveArticles() {
            return window.App.put(
                `/web-api/newsletter/${this.newsletter.id}?include=articles`,
                this.form,
                null,
                ["articles", "filler_as_cover"]
            ).then((response) => {
                _.forEach(response.data.articles.data, (article, index) => {
                    this.form.articles[index].id = article.id;
                });
                return response;
            });
        },
        saveFillerArticles() {
            return window.App.put(
                `/web-api/newsletter/${this.newsletter.id}`,
                this.form,
                null,
                ["filler_articles"]
            ).then((response) => {
                return response;
            });
        },
        saveLists() {
            return window.App.put(
                `/web-api/newsletter/${this.newsletter.id}?include=special_events,list_two_items`,
                this.form,
                null,
                [
                    "list_1_headline",
                    "list_2_headline",
                    "special_events",
                    "list_two_items"
                ]
            ).then((response) => {
                this.form.special_events = [];
                _.forEach(
                    response.data.special_events.data,
                    (special_event, index) => {
                        this.form.special_events.push(
                            _.pick(special_event, [
                                "id",
                                "value_1",
                                "value_2",
                                "newsletter_id"
                            ])
                        );
                    }
                );
                this.form.list_two_items = [];
                _.forEach(response.data.list_two_items.data, (item, index) => {
                    this.form.list_two_items.push(
                        _.pick(item, ["id", "value", "newsletter_id"])
                    );
                });
                return response;
            });
        },
        saveDeptListItems() {
            return window.App.put(
                `/web-api/newsletter/${this.newsletter.id}?include=dept_list_items`,
                this.form,
                null,
                ["dept_list_headline", "dept_list_items"]
            ).then((response) => {
                this.form.dept_list_items = [];
                _.forEach(response.data.dept_list_items.data, (item, index) => {
                    this.form.dept_list_items.push(
                        _.pick(item, [
                            "id",
                            "newsletter_id",
                            "value_1",
                            "value_2"
                        ])
                    );
                });
                return response;
            });
        },
        getStepStatus(i) {
            if (i + 1 === this.currentStep) {
                return "process";
            } else if (i + 1 < this.currentStep) {
                return null;
            }
            return "wait";
        },
        /**
         * Saves the newsletter with the changed form attributes.
         * @returns {Promise} Axios response promise
         */
        saveNewsletter() {
            let attrs = this.getChangedFormAttributes();
            let include = attrs.join(",");
            return window.App.put(
                `/web-api/newsletter/${this.newsletter.id}?include=${include}`,
                this.form,
                null,
                attrs
            ).then((response) => {
                this.form.proofed = response.data.proofed;
                if (attrs.includes("articles")) {
                    _.forEach(response.data.articles.data, (article, index) => {
                        this.form.articles[index].id = article.id;
                    });
                }
                if (attrs.includes("special_events")) {
                    _.forEach(
                        response.data.special_events.data,
                        (special_event, index) => {
                            this.form.special_events[index].id =
                                special_event.id;
                        }
                    );
                }
                if (attrs.includes("list_two_items")) {
                    _.forEach(
                        response.data.list_two_items.data,
                        (item, index) => {
                            this.form.list_two_items[index].id = item.id;
                        }
                    );
                }
                if (attrs.includes("dept_list_items")) {
                    _.forEach(
                        response.data.dept_list_items.data,
                        (item, index) => {
                            this.form.dept_list_items[index].id = item.id;
                        }
                    );
                }
                return response;
            });
        },
        /**
         * Tracks user typing activity to prevent auto-save during active editing
         */
        trackTypingActivity() {
            this.lastTypingTimestamp = Date.now();
            this.typing_active = true;

            // Clear the typing indicator after a delay
            clearTimeout(this._typingTimeout);
            this._typingTimeout = setTimeout(() => {
                this.typing_active = false;
            }, 3000);
        },
        /**
         * Initializes the auto-save feature that saves changes every 60 seconds.
         * Includes debouncing to prevent saving while user is actively typing.
         */
        initAutoSave() {
            this.stopAutoSave();
            this.auto_save_interval = setInterval(() => {
                if (!this.hasChanges) {
                    return;
                }

                if (this.form.busy) {
                    return;
                }

                if (
                    this.hasValidationErrors ||
                    (this.$refs.articles &&
                        this.$refs.articles.hasValidationErrors)
                ) {
                    console.log("has validation errors");
                    return;
                }

                // Skip auto-save if user is actively typing
                if (
                    this.lastTypingTimestamp &&
                    Date.now() - this.lastTypingTimestamp < 3000
                ) {
                    console.log(
                        "User is actively typing, postponing auto-save"
                    );
                    return;
                }

                this.auto_saving = true;
                this.show_auto_save_message = false;
                this.show_auto_save_error = false;
                this.show_auto_save_validation_message = false;

                let doneAutoSave = () => {
                    this.initOldData();
                    this.auto_saving = false;
                    this.show_auto_save_message = true;
                    this.show_auto_save_error = false; // Explicitly clear error state
                    this.show_auto_save_validation_message = false;
                    setTimeout(() => {
                        this.show_auto_save_message = false;
                    }, 5000);
                };

                let errorAutoSave = (e) => {
                    console.error(e);
                    this.auto_saving = false;

                    if (e.response && e.response.status === 422) {
                        this.show_auto_save_validation_message = true;
                        this.show_auto_save_error = false;
                        setTimeout(() => {
                            this.show_auto_save_validation_message = false;
                        }, 5000);
                    } else {
                        this.show_auto_save_error = true;
                        this.show_auto_save_validation_message = false;
                        setTimeout(() => {
                            this.show_auto_save_error = false;
                        }, 5000);
                    }
                };

                if (this.getChangedFormAttributes().length) {
                    this.saveNewsletter()
                        .then(doneAutoSave)
                        .catch(errorAutoSave);
                }
            }, 60 * 1000);
        },
        stopAutoSave() {
            if (this.auto_save_interval) {
                clearInterval(this.auto_save_interval);
                this.auto_save_interval = null;
            }
        },
        closePopup() {
            if (this.$refs.articles) {
                this.$refs.articles.closePopup();
            }
        },
        clickStep(index) {
            this.currentStep = index + 1;
            this.closePopup();
        }
    }
};
</script>

<style lang="scss" scoped>
@import "../../../../assets/scss/_variables";
.el-steps.creation-steps {
    flex-grow: 1;

    a {
        font-weight: bold;
        text-decoration: none;
    }

    ::v-deep .el-step__head.is-process .el-step__icon.is-text {
        border-color: $text-color-accent !important;
        background-color: $text-color-accent !important;
    }
    
    ::v-deep .el-step.is-center .el-step__description {
        padding-left: 0;
        padding-right: 0;
    }
}

.action-buttons {
    margin-top: 120px;
    margin-bottom: 10px;
    position: relative;
}

@media screen and (max-width: 1320px) {
    .action-buttons {
        margin-top: 140px;
    }
}
</style>
